"use client";

import { useMemo, useState } from "react";
import { cn } from "@/lib/format";
import { TeamCard } from "@/components/card/TeamCard";
import type { TeamMemberList } from "@/lib/dto";

type Entry = { member: TeamMemberList; href: string };
type Props = { members: Entry[] };

// Fixed department tabs, in display order. Names are matched against
// `member.department.name` case-insensitively since department names are
// free-text CMS fields, not an enum.
const DEPARTMENT_TABS = [
  "Executive Team",
  "Trekking Leaders",
  "Tour Guides",
  "Climbing Leaders",
  "Medical Staff",
] as const;

/**
 * Client-side department tabs for the team listing. The tab set is fixed
 * (not derived from the data) and always defaults to "Executive Team".
 * Switching tabs filters the grid instantly with no reload.
 */
export function TeamDepartmentTabs({ members }: Props) {
  const [active, setActive] = useState<string>(DEPARTMENT_TABS[0]);
  const visible = useMemo(
    () =>
      members.filter(
        (e) =>
          e.member.department?.name?.toLowerCase() === active.toLowerCase(),
      ),
    [members, active],
  );

  return (
    <>
      <div
        role="tablist"
        aria-label="Filter team by department"
        className="relative px-3 mb-6 flex w-full items-center gap-x-1 overflow-x-auto after:absolute after:inset-x-0 after:bottom-0 after:-z-10 after:h-px after:bg-headings/30"
      >
        {DEPARTMENT_TABS.map((name) => (
          <TabButton
            key={name}
            active={active === name}
            onClick={() => setActive(name)}
            label={name}
          />
        ))}
      </div>

      <ul className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {visible.map(({ member, href }) => (
          <li key={member.id}>
            <TeamCard member={member} href={href} />
          </li>
        ))}
      </ul>
    </>
  );
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "relative block shrink-0 rounded-t-md border border-b-0 border-headings/30 px-3 py-1.5 text-xs font-extrabold md:px-6 md:py-2.5 md:text-sm",
        active
          ? "bg-bg text-primary shadow-none"
          : "bg-primary/5 text-headings hover:text-primary",
      )}
    >
      {label}
    </button>
  );
}
